import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { CognitivePsychosocialFormField } from './formField'

/**
 * Cognitive/Psychosocial Development subsection adapter
 * Handles communication, cognitive assessment, and substance abuse history (5 fields)
 */
export class CognitivePsychosocialAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cognitive/Psychosocial assessment fields
    const communicationAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT
    )

    const cognitiveAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT
    )

    const historySubstanceAbuse = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE
    )?.at(0)

    const substanceAbuseNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT]:
        communicationAssessment,
      [CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT]:
        cognitiveAssessment,
      [CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE]:
        historySubstanceAbuse,
      [CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES]:
        substanceAbuseNotes,
      [CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT,
        value: this.getFormDataField(
          CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT
        )
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT,
        value: this.getFormDataField(
          CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT
        )
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY,
        value: this.getFormDataField(
          CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY
        )
      }
    ]

    if (
      this.formDataFieldExists(
        CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE
      )
    ) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE,
        value: this.getFormDataField(
          CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE
        )
      })
    }

    // Conditionally add substance abuse notes if they exist (they are conditionally rendered)
    if (
      this.formDataFieldExists(
        CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES
      )
    ) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES,
        value: this.getFormDataField(
          CognitivePsychosocialFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES
        )
      })
    }

    return mutations
  }
}
